import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { jwtDecode } from "jwt-decode";
import { API } from "../config/apiConfig";
import { portal, portalGradientTextSx } from "../styles/dashboardBrand";
import DashboardPageHeader from "../components/DashboardPageHeader";
import { useUtcMidnightCountdown, formatUtcMidnightCountdown } from "../hooks/useUtcMidnightCountdown";
import { dreameyDiamondsUserSum } from "../utils/dreameyDiamonds";
import {
  readHomeCache,
  writeHomeCache,
  shouldForceUserDashboardHomeNetwork,
  clearUserDashboardHomeForceNetwork,
} from "../utils/userDashboardHomeCache";
import { buildReferralsActivitySnapshot } from "../utils/referralsActivitySnapshot";

function designationNameFromRecord(record) {
  if (!record || typeof record !== "object") return "";
  const raw =
    record.DesignationName ??
    record.designationName ??
    record.DESIGNATIONNAME;
  const s = String(raw ?? "").trim();
  if (!s || s.toLowerCase() === "null") return "";
  return s;
}

function normalizeStreakPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  return {
    adsWatchedToday: Number(r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0) || 0,
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 10) || 10,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
  };
}

/** Per-referral streak (same fields as Activity page) for dashboard cache / snapshot. */
function normalizeStreakPayloadForRef(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw.data && typeof raw.data === "object" ? raw.data : raw;
  const last30 =
    r.last30Days ?? r.Last30Days ?? r.last_30_days ?? [];
  return {
    adsWatchedToday: Number(
      r.adsWatchedToday ?? r.adsToday ?? r.AdsWatchedToday ?? r.watchedToday ?? 0
    ),
    goalPerDay: Number(r.goalPerDay ?? r.goal ?? r.GoalPerDay ?? r.dailyGoal ?? 5) || 5,
    currentStreak: Number(r.currentStreak ?? r.CurrentStreak ?? r.consecutiveDays ?? 0) || 0,
    longestStreak: Number(r.longestStreak ?? r.LongestStreak ?? r.bestStreak ?? 0) || 0,
    last30Days: Array.isArray(last30) ? last30 : [],
  };
}

/** True when today's ad goal is met (streak uses UTC day from API). */
function isStreakGoalMetToday(adsWatchedToday, goalPerDay) {
  const g = Math.max(1, Number(goalPerDay) || 10);
  return Number(adsWatchedToday ?? 0) >= g;
}

function displayNameFromRecord(obj, fallback = "Member") {
  const first = String(obj?.FirstName ?? obj?.firstName ?? "").trim();
  const last = String(obj?.LastName ?? obj?.lastName ?? "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  const userName = String(obj?.UserName ?? obj?.userName ?? obj?.username ?? "").trim();
  if (userName) return userName;
  return fallback;
}

/**
 * Rank label from main.Designation (API). Tree "Level" is placement depth only — never use it as a rank title.
 * If Users.DesignationID is NULL, there is no designation row; show Member (or Leader for self when defaultLeader).
 */
/** Lower index = higher tier (Diamond first, Member last). Used only for referral list ordering. */
const REFERRAL_RANK_SORT_ORDER = [
  "Diamond Executive",
  "Gold Executive",
  "Silver Executive",
  "Team Leader",
  "Leader",
  "Member",
];

function referralRankSortIndex(rankLabel) {
  const s = String(rankLabel ?? "")
    .trim()
    .toLowerCase();
  if (!s) return REFERRAL_RANK_SORT_ORDER.length;
  for (let i = 0; i < REFERRAL_RANK_SORT_ORDER.length; i++) {
    if (s === REFERRAL_RANK_SORT_ORDER[i].toLowerCase()) return i;
  }
  if (s.includes("diamond")) return 0;
  if (s.includes("gold") && s.includes("executive")) return 1;
  if (s.includes("silver") && s.includes("executive")) return 2;
  if (s.includes("team") && s.includes("leader")) return 3;
  if (s === "leader") return 4;
  if (s.includes("member")) return 5;
  return REFERRAL_RANK_SORT_ORDER.length;
}

function resolveRankLabel(record, designationByUserId, userId, defaultLeader = false) {
  const fromDb = designationNameFromRecord(record);
  if (fromDb) return fromDb;
  const uid = Number(userId);
  if (Number.isFinite(uid) && designationByUserId && typeof designationByUserId === "object") {
    const fromMap = String(designationByUserId[uid] ?? "").trim();
    if (fromMap && fromMap.toLowerCase() !== "null") return fromMap;
  }
  const fromRankFields = String(
    record?.ReferralRank ??
      record?.referralRank ??
      record?.RankName ??
      record?.rankName ??
      record?.RankTitle ??
      record?.rankTitle ??
      ""
  ).trim();
  if (fromRankFields) return fromRankFields;
  return defaultLeader ? "Leader" : "Member";
}

function getEngagementPointsFromEarnings(earnings) {
  if (!earnings || typeof earnings !== "object") return 0;
  const raw =
    earnings.CurrentMonthTokens ??
    earnings.currentMonthTokens ??
    earnings.TotalEngagementPoints ??
    earnings.EngagementPoints ??
    earnings.TotalTokens ??
    earnings.Tokens ??
    earnings.TotalEarnings ??
    0;
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

function getUserIdFromToken() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    const userId = Number(decoded?.userId ?? decoded?.UserID ?? decoded?.id);
    return Number.isFinite(userId) ? userId : null;
  } catch {
    return null;
  }
}

/** Same tier thresholds as Activity (Leader → Diamond) — network depth & direct refs. */
const RANK_BAR_DEPTH_THRESHOLDS = [3, 6, 9, 12, 15];
const RANK_BAR_REFERRAL_THRESHOLDS = [2, 4, 6, 8, 10];

function rankMetricToBarFillPercent(rawValue, thresholdsAscending) {
  const d = Math.max(0, Number(rawValue) || 0);
  const tiers = thresholdsAscending;
  const n = tiers.length;
  if (n === 0) return 0;
  const segW = 100 / n;
  if (d >= tiers[n - 1]) return 100;
  if (d < tiers[0]) return tiers[0] > 0 ? (d / tiers[0]) * segW : 0;
  for (let k = 0; k < n - 1; k++) {
    if (d >= tiers[k] && d < tiers[k + 1]) {
      const span = tiers[k + 1] - tiers[k];
      const t = span > 0 ? (d - tiers[k]) / span : 0;
      return (k + 1) * segW + t * segW;
    }
  }
  return 100;
}

function rankLevelsToBarFillPercent(level) {
  return rankMetricToBarFillPercent(level, RANK_BAR_DEPTH_THRESHOLDS);
}

function rankReferralsToBarFillPercent(directRefCount) {
  return rankMetricToBarFillPercent(directRefCount, RANK_BAR_REFERRAL_THRESHOLDS);
}

function maxDepthFromNestedReferralNodes(nodes, depth = 1) {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let best = depth;
  for (const n of nodes) {
    const children = n.children;
    if (Array.isArray(children) && children.length > 0) {
      best = Math.max(best, maxDepthFromNestedReferralNodes(children, depth + 1));
    }
  }
  return best;
}

function maxPlacementDepthFromDirectReferralsResponse(data) {
  if (!data || typeof data !== "object") return 0;
  const flat = data.flat;
  if (Array.isArray(flat) && flat.length > 0) {
    let m = 0;
    for (const r of flat) {
      const lv = Number(r.Level ?? r.level ?? 0) || 0;
      if (lv > m) m = lv;
    }
    return m;
  }
  const refs = data.referrals;
  if (!Array.isArray(refs) || refs.length === 0) return 0;
  return maxDepthFromNestedReferralNodes(refs, 1);
}

/** Compact label: `hostname/refCode` from `?ref=` — full URL still used for copy/open. */
function referralLinkShortDisplay(fullUrl) {
  if (!fullUrl || typeof fullUrl !== "string") return "";
  try {
    const u = new URL(fullUrl);
    const host = u.hostname.replace(/^www\./i, "");
    const ref = u.searchParams.get("ref") ?? u.searchParams.get("REF");
    if (host && ref) {
      return `${host}/${ref}`;
    }
    const path = u.pathname.replace(/^\/+/, "");
    const fallback = `${path}${u.search || ""}`;
    return fallback || fullUrl;
  } catch {
    return fullUrl;
  }
}

function extractNumericLevelFromRecord(obj) {
  if (!obj || typeof obj !== "object") return null;
  const keys = [
    "Level",
    "level",
    "UserLevel",
    "userLevel",
    "CurrentLevel",
    "currentLevel",
    "MarketingLevel",
    "marketingLevel",
    "ReferralProgramLevel",
    "referralProgramLevel",
  ];
  for (const k of keys) {
    if (obj[k] == null || obj[k] === "") continue;
    const n = Number(obj[k]);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function resolveUserProgramLevel(profileUser, jwtPayload, levelByUserId, selfUserId) {
  const fromMap =
    selfUserId != null && levelByUserId && typeof levelByUserId === "object"
      ? levelByUserId[Number(selfUserId)]
      : null;
  const fromMapNum = fromMap != null ? Number(fromMap) : NaN;
  const merged = {
    ...(profileUser && typeof profileUser === "object" ? profileUser : {}),
    ...(jwtPayload && typeof jwtPayload === "object" ? jwtPayload : {}),
  };
  const fromProfile = extractNumericLevelFromRecord(merged);
  const candidates = [];
  if (Number.isFinite(fromMapNum)) candidates.push(fromMapNum);
  if (fromProfile != null && Number.isFinite(fromProfile)) candidates.push(fromProfile);
  if (candidates.length === 0) return 0;
  return Math.max(...candidates);
}

/** Small caps label for sub-sections inside dashboard cards */
function SectionEyebrow({ children, sx = {} }) {
  return (
    <Typography
      variant="overline"
      component="p"
      sx={{
        m: 0,
        letterSpacing: "0.14em",
        fontSize: "0.65rem",
        fontWeight: 600,
        color: "text.secondary",
        opacity: 0.88,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/** One row (name | pills) until the name would ellipsize; then stacks pills on the next row so the full name can show. Per-card only. */
function ReferralCardNamePillsRow({ referral, theme, isDark, referralLayoutChars }) {
  const containerRef = useRef(null);
  const nameRef = useRef(null);
  const [stackPills, setStackPills] = useState(false);
  const [layoutTick, setLayoutTick] = useState(0);

  const nameText = String(referral.name ?? "").trim() || "—";

  const measureShouldStack = useCallback(() => {
    const el = nameRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 0.5) {
      setStackPills(true);
    }
  }, []);

  useLayoutEffect(() => {
    setStackPills(false);
  }, [nameText, referral.userId]);

  useLayoutEffect(() => {
    if (stackPills) return;
    measureShouldStack();
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      measureShouldStack();
      raf2 = requestAnimationFrame(measureShouldStack);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 != null) cancelAnimationFrame(raf2);
    };
  }, [stackPills, nameText, referral.rank, referral.currentStreak, layoutTick, measureShouldStack]);

  useEffect(() => {
    const onResize = () => {
      setStackPills(false);
      setLayoutTick((n) => n + 1);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) {
        setStackPills(false);
        setLayoutTick((n) => n + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [nameText]);

  const pills = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="nowrap"
      spacing={1}
      sx={{
        gap: { xs: 0.45, sm: 0.65 },
        flexShrink: 0,
        width: stackPills ? "100%" : "auto",
        minWidth: 0,
        justifySelf: "end",
      }}
    >
      <Box
        sx={{
          border: "1px solid",
          borderColor: alpha(theme.palette.success.main, isDark ? 0.35 : 0.22),
          borderRadius: 999,
          px: { xs: 0.55, sm: 0.9, md: 1.1 },
          py: 0.45,
          flexShrink: 0,
          maxWidth: "100%",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#152018", 0.95)} 0%, ${alpha("#0f1510", 0.98)} 100%)`
            : alpha(theme.palette.success.main, 0.07),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: theme.palette.success.light,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textAlign: "right",
            letterSpacing: "0.02em",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            maxWidth: {
              xs: "min(9.5rem, 36vw)",
              md: `${referralLayoutChars.rankChars}ch`,
            },
          }}
        >
          {referral.rank}
        </Typography>
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: isStreakGoalMetToday(referral.adsWatchedToday, referral.goalPerDay)
            ? alpha(theme.palette.divider, isDark ? 0.35 : 0.2)
            : alpha(theme.palette.warning.main, 0.45),
          borderRadius: 999,
          px: { xs: 0.55, sm: 0.9, md: 1.1 },
          py: 0.45,
          flexShrink: 0,
          maxWidth: "100%",
          background: isDark
            ? `linear-gradient(135deg, ${alpha("#1c1618", 0.95)} 0%, ${alpha("#141012", 0.98)} 100%)`
            : alpha(theme.palette.background.paper, 0.95),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: "common.white",
            textAlign: "right",
            letterSpacing: "0.03em",
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          }}
        >
          {referral.currentStreak} 🔥
        </Typography>
      </Box>
    </Stack>
  );

  if (stackPills) {
    return (
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.85,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          ref={nameRef}
          variant="body2"
          component="span"
          sx={{
            fontWeight: 700,
            color: "common.white",
            width: "100%",
            display: "block",
            whiteSpace: "normal",
            overflow: "visible",
            wordBreak: "break-word",
            textDecoration: "none",
            letterSpacing: "0.02em",
            fontSize: "0.9375rem",
            textAlign: "left",
          }}
        >
          {nameText}
        </Typography>
        {pills}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "center",
        columnGap: { xs: 0.5, sm: 0.75 },
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        ref={nameRef}
        variant="body2"
        component="span"
        sx={{
          fontWeight: 700,
          color: "common.white",
          minWidth: 0,
          maxWidth: "100%",
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textDecoration: "none",
          letterSpacing: "0.02em",
          fontSize: "0.9375rem",
          textAlign: "left",
        }}
      >
        {nameText}
      </Typography>
      {pills}
    </Box>
  );
}

function ThinLadderBar({ theme, emoji, fillPercent, label }) {
  const fp = Math.min(100, Math.max(0, fillPercent));
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
      <Typography
        component="span"
        sx={{
          fontSize: "1.05rem",
          lineHeight: 1,
          flexShrink: 0,
          opacity: 0.92,
          filter: isDark ? "drop-shadow(0 0 8px rgba(79,172,254,0.25))" : "none",
        }}
        aria-hidden
      >
        {emoji}
      </Typography>
      <Box
        component="span"
        sx={{ position: "relative", flex: 1, minWidth: 0 }}
        aria-label={label}
      >
        <Box
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.07 : 0.06),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, isDark ? 0.5 : 0.12),
            overflow: "hidden",
            boxShadow: isDark ? `inset 0 1px 2px ${alpha("#000", 0.35)}` : `inset 0 1px 1px ${alpha("#000", 0.04)}`,
          }}
        >
          <Box
            sx={{
              width: `${fp}%`,
              height: "100%",
              borderRadius: "inherit",
              background: portal.gradientBar,
              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow:
                fp > 0
                  ? `0 0 14px ${alpha(portal.cyan, isDark ? 0.35 : 0.2)}, inset 0 1px 0 ${alpha("#fff", 0.12)}`
                  : "none",
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: alpha("#fff", 0.06),
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800, color: "common.white" }}>
        {value}
      </Typography>
    </Stack>
  );
}

function getInitialHomeStateFromCache() {
  const uid = getUserIdFromToken();
  if (!uid) {
    return { loading: false, selfInfo: null, referrals: [], error: "" };
  }
  if (shouldForceUserDashboardHomeNetwork()) {
    return { loading: true, selfInfo: null, referrals: [], error: "" };
  }
  const c = readHomeCache(uid);
  if (c?.selfInfo) {
    return { loading: false, selfInfo: c.selfInfo, referrals: c.referrals, error: "" };
  }
  return { loading: true, selfInfo: null, referrals: [], error: "" };
}

export default function Home() {
  const theme = useTheme();
  const utcMidnightRemainingMs = useUtcMidnightCountdown();
  const _init = getInitialHomeStateFromCache();
  const [loading, setLoading] = useState(_init.loading);
  const [error, setError] = useState(_init.error);
  const [selfInfo, setSelfInfo] = useState(_init.selfInfo);
  const [referrals, setReferrals] = useState(_init.referrals);
  const [copySnack, setCopySnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const copyToClipboard = useCallback(async (text, successMessage) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopySnack({
        open: true,
        message: successMessage || "Copied",
        severity: "success",
      });
    } catch {
      setCopySnack({
        open: true,
        message: "Could not copy",
        severity: "error",
      });
    }
  }, []);

  const referralLayoutChars = useMemo(() => {
    if (!Array.isArray(referrals) || referrals.length === 0) {
      return { rankChars: 6 };
    }
    const maxRank = referrals.reduce(
      (m, r) => Math.max(m, Math.min(16, String(r?.rank ?? "").length)),
      0
    );
    return {
      rankChars: Math.max(1, maxRank),
    };
  }, [referrals]);

  const selfStatsLayoutChars = useMemo(() => {
    if (!selfInfo) return { rankChars: 6 };
    const rankLen = String(selfInfo.rank ?? "").length;
    return {
      rankChars: Math.max(1, Math.min(16, rankLen)),
    };
  }, [selfInfo]);

  const anyDownlineAdGoalOpen = useMemo(
    () =>
      referrals.some((r) => !isStreakGoalMetToday(r.adsWatchedToday, r.goalPerDay)),
    [referrals]
  );

  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  const userId = useMemo(() => getUserIdFromToken(), [token]);

  const fetchHomeData = useCallback(async () => {
    if (!userId) {
      setError("Please sign in to view Home.");
      setLoading(false);
      return;
    }

    const forceNetwork = shouldForceUserDashboardHomeNetwork();
    const cached = readHomeCache(userId);
    if (!forceNetwork && cached?.selfInfo) {
      setSelfInfo(cached.selfInfo);
      setReferrals(cached.referrals);
      setError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [
        profileRes,
        earningsRes,
        streakRes,
        refsRes,
        levelsRes,
        coinsRes,
        streakCompletersRes,
      ] = await Promise.all([
        fetch(API.userProfile(userId), { headers }),
        fetch(`${API.earningsSummary}/${userId}`, { headers }),
        fetch(`${API.adsStreak}?userId=${encodeURIComponent(userId)}`, { headers }),
        fetch(API.originalReferrals(userId), { headers }),
        fetch(API.getUsersByLevel, { headers }),
        fetch(API.appCoinsApps, { headers }),
        fetch(API.directReferralsStreakCompleters, { headers }),
      ]);

      const profileData = await profileRes.json().catch(() => ({}));
      const earningsData = await earningsRes.json().catch(() => ({}));
      const streakData = await streakRes.json().catch(() => ({}));
      const refsData = await refsRes.json().catch(() => ({}));
      const levelsData = await levelsRes.json().catch(() => ({}));
      const coinsData = await coinsRes.json().catch(() => ({}));
      const streakCompletersData = await streakCompletersRes.json().catch(() => ({}));

      if (!profileRes.ok) {
        throw new Error(profileData?.message || "Failed to load profile");
      }

      const me = profileData?.user ?? profileData ?? {};
      const earnings = earningsData?.earnings ?? earningsData?.data ?? earningsData ?? {};
      const streakNorm = normalizeStreakPayload(streakData);
      const myStreak = streakNorm?.currentStreak ?? 0;
      const directRefs = Array.isArray(refsData?.referrals) ? refsData.referrals : [];

      const levelByUserId = {};
      const designationByUserId = {};
      if (levelsRes.ok && levelsData?.success && Array.isArray(levelsData?.users)) {
        for (const u of levelsData.users) {
          const uid = Number(u?.UserID ?? u?.userId);
          if (!Number.isFinite(uid)) continue;
          levelByUserId[uid] = Number(u?.Level ?? u?.level ?? 0) || 0;
          const dn = designationNameFromRecord(u);
          if (dn) designationByUserId[uid] = dn;
        }
      }

      const selfRank = resolveRankLabel(
        me,
        designationByUserId,
        userId,
        directRefs.length > 0
      );

      let placementMaxDepth = null;
      const refCode = String(me?.ReferralCode ?? me?.referralCode ?? "").trim();
      if (refCode) {
        try {
          const treeRes = await fetch(API.directReferrals(encodeURIComponent(refCode)), { headers });
          const treeData = await treeRes.json().catch(() => ({}));
          if (treeRes.ok) {
            placementMaxDepth = maxPlacementDepthFromDirectReferralsResponse(treeData);
          }
        } catch {
          /* network tree optional for home bars */
        }
      }

      let jwtPayload = null;
      try {
        jwtPayload = token ? jwtDecode(token) : null;
      } catch {
        jwtPayload = null;
      }
      const legacyProgramLevel = resolveUserProgramLevel(me, jwtPayload, levelByUserId, userId);
      const rankQualifyingLevel =
        placementMaxDepth != null ? Math.max(legacyProgramLevel, placementMaxDepth) : legacyProgramLevel;
      const directRefCount = directRefs.length;
      const levelsBarPercent = rankLevelsToBarFillPercent(rankQualifyingLevel);
      const referralsBarPercent = rankReferralsToBarFillPercent(directRefCount);
      const todayGoalPercent =
        (streakNorm?.goalPerDay ?? 0) > 0
          ? Math.min(100, ((streakNorm?.adsWatchedToday ?? 0) / (streakNorm?.goalPerDay ?? 1)) * 100)
          : 0;

      const myReferralCode = (
        refCode ||
        String(jwtPayload?.referralCode ?? jwtPayload?.ReferralCode ?? "").trim()
      );
      const myReferralLink = myReferralCode
        ? `https://dv-apps.com/signup?ref=${encodeURIComponent(myReferralCode)}`
        : "";

      let totalDreameyDiamonds = 0;
      if (coinsRes.ok && coinsData?.success) {
        totalDreameyDiamonds = dreameyDiamondsUserSum(coinsData);
      }

      const refStreakByUserId = new Map();

      const referralRows = await Promise.all(
        directRefs.map(async (ref) => {
          const rid = Number(ref?.UserID ?? ref?.userId);
          if (!Number.isFinite(rid)) return null;

          const [refEarningsRes, refStreakRes] = await Promise.all([
            fetch(`${API.earningsSummary}/${rid}`, { headers }).catch(() => null),
            fetch(`${API.adsStreak}?userId=${encodeURIComponent(rid)}`, { headers }).catch(() => null),
          ]);

          let refEngagement = 0;
          if (refEarningsRes && refEarningsRes.ok) {
            const refEarningsData = await refEarningsRes.json().catch(() => ({}));
            const refEarnings = refEarningsData?.earnings ?? refEarningsData?.data ?? refEarningsData ?? {};
            refEngagement = getEngagementPointsFromEarnings(refEarnings);
          }

          let refCurrentStreak = 0;
          let refAdsWatchedToday = 0;
          let refGoalPerDay = 10;
          if (refStreakRes && refStreakRes.ok) {
            const refStreakData = await refStreakRes.json().catch(() => ({}));
            const refFull = normalizeStreakPayloadForRef(refStreakData);
            if (refFull) refStreakByUserId.set(rid, refFull);
            const refStreakNorm = normalizeStreakPayload(refStreakData);
            refCurrentStreak = refStreakNorm?.currentStreak ?? 0;
            refAdsWatchedToday = refStreakNorm?.adsWatchedToday ?? 0;
            refGoalPerDay = refStreakNorm?.goalPerDay ?? 10;
          }
          const refTodayGoalPercent =
            refGoalPerDay > 0 ? Math.min(100, (refAdsWatchedToday / refGoalPerDay) * 100) : 0;

          return {
            userId: rid,
            name: displayNameFromRecord(ref),
            rank: resolveRankLabel(ref, designationByUserId, rid, false),
            engagementPoints: refEngagement,
            currentStreak: refCurrentStreak,
            adsWatchedToday: refAdsWatchedToday,
            goalPerDay: refGoalPerDay,
            todayGoalPercent: refTodayGoalPercent,
          };
        })
      );

      const rows = referralRows.filter(Boolean);
      const goalMet = (r) => {
        const g = Math.max(1, Number(r.goalPerDay) || 10);
        return Number(r.adsWatchedToday ?? 0) >= g;
      };
      const sortedReferrals = [...rows].sort((a, b) => {
        const ma = goalMet(a);
        const mb = goalMet(b);
        if (ma !== mb) return Number(ma) - Number(mb);
        const ga = Math.max(1, Number(a.goalPerDay) || 10);
        const gb = Math.max(1, Number(b.goalPerDay) || 10);
        const ra = Number(a.adsWatchedToday ?? 0) / ga;
        const rb = Number(b.adsWatchedToday ?? 0) / gb;
        if (rb !== ra) return rb - ra;
        const rankDiff = referralRankSortIndex(a.rank) - referralRankSortIndex(b.rank);
        if (rankDiff !== 0) return rankDiff;
        return (b.todayGoalPercent ?? 0) - (a.todayGoalPercent ?? 0);
      });

      let referralsActivity = null;
      if (refsRes.ok && levelsRes.ok) {
        try {
          referralsActivity = buildReferralsActivitySnapshot({
            listData: refsData,
            streakCompletersData: streakCompletersRes.ok ? streakCompletersData : {},
            levelData: levelsData,
            refStreakByUserId,
          });
        } catch {
          referralsActivity = null;
        }
      }

      const nextSelfInfo = {
        name: displayNameFromRecord(me, "You"),
        rank: selfRank,
        engagementPoints: getEngagementPointsFromEarnings(earnings),
        currentStreak: myStreak,
        levelsBarPercent,
        referralsBarPercent,
        todayGoalPercent,
        adsWatchedToday: streakNorm?.adsWatchedToday ?? 0,
        goalPerDay: streakNorm?.goalPerDay ?? 10,
        referralLink: myReferralLink,
        totalDreameyDiamonds,
      };
      setSelfInfo(nextSelfInfo);
      setReferrals(sortedReferrals);
      writeHomeCache(userId, nextSelfInfo, sortedReferrals, {
        profile: profileData,
        earnings: earningsData,
        streak: streakData,
        refs: refsData,
        levels: levelsData,
        coins: coinsData,
        streakCompleters: streakCompletersData,
        referralsActivity,
        placementMaxDepth,
      });
      clearUserDashboardHomeForceNetwork();
    } catch (e) {
      setError(e?.message || "Failed to load home data");
      setSelfInfo(null);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  useEffect(() => {
    void fetchHomeData();
  }, [fetchHomeData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isDark = theme.palette.mode === "dark";
  const homeTrailLabelSx = {
    display: "block",
    lineHeight: 1.2,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "text.secondary",
    opacity: 0.85,
  };
  const homeGlassSurface = (extra = {}) => ({
    borderRadius: 2.5,
    px: 1.35,
    py: 0.85,
    border: "1px solid",
    borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.22 : 0.14),
    background: isDark
      ? `linear-gradient(155deg, ${alpha("#1e2436", 0.92)} 0%, ${alpha("#12151f", 0.88)} 55%, ${alpha("#0d0f16", 0.95)} 100%)`
      : alpha(theme.palette.background.paper, 0.98),
    backdropFilter: "blur(14px)",
    boxShadow: isDark
      ? `0 4px 24px ${alpha("#000", 0.35)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`
      : `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
    ...extra,
  });

  return (
    <Box
      sx={{
        maxWidth: 920,
        mx: "auto",
        py: { xs: 1.5, sm: 2.75 },
        px: { xs: 1, sm: 2 },
        background: isDark
          ? `linear-gradient(180deg, ${alpha(portal.indigo, 0.07)} 0%, transparent 32%, transparent 100%)`
          : `linear-gradient(180deg, ${alpha(portal.cyan, 0.04)} 0%, transparent 40%)`,
      }}
    >
      <DashboardPageHeader
        icon={HomeIcon}
        title="Home"
        titleTrail={
          selfInfo ? (
            <Box
              sx={{
                transform: { md: "translateY(-0.15rem)" },
                minWidth: 0,
                pt: 0,
                mt: 0,
                textAlign: "left",
                width: "fit-content",
                maxWidth: "100%",
                alignSelf: "flex-start",
                ...homeGlassSurface(),
              }}
            >
              <Typography variant="caption" sx={homeTrailLabelSx}>
                Your referral link
              </Typography>
              {selfInfo.referralLink ? (
                <Link
                  href={selfInfo.referralLink}
                  onClick={(e) => {
                    e.preventDefault();
                    void copyToClipboard(selfInfo.referralLink, "Referral link copied");
                  }}
                  variant="body2"
                  title={`Click to copy: ${selfInfo.referralLink}`}
                  aria-label={`Copy full referral link ${selfInfo.referralLink}`}
                  sx={{
                    wordBreak: "break-all",
                    cursor: "pointer",
                    display: "inline-block",
                    mt: 0.35,
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    color: portal.cyan,
                    textDecoration: "none",
                    borderBottom: `1px solid ${alpha(portal.cyan, 0.35)}`,
                    transition: "color 0.2s, border-color 0.2s",
                    "&:hover": {
                      color: portal.indigo,
                      borderBottomColor: alpha(portal.indigo, 0.45),
                    },
                  }}
                >
                  {referralLinkShortDisplay(selfInfo.referralLink)}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }} component="span" display="block">
                  —
                </Typography>
              )}
            </Box>
          ) : null
        }
      />

      <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
        <Paper
          component={RouterLink}
          to="/dashboard/apps"
          elevation={0}
          sx={(theme) => ({
            display: "inline-block",
            width: "fit-content",
            textDecoration: "none",
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(255,255,255,0.8)",
            backdropFilter: "blur(10px)",
            border:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(255,255,255,0.2)",
            transition: "background 0.2s, border-color 0.2s",
            "&:hover": {
              background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.95)",
              borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.4)",
            },
            "@keyframes popInOut": {
              "0%, 200%": { transform: "scale(1)", opacity: 1.1 },
              "50%": { transform: "scale(1.08)", opacity: 0.8 },
            },
            animation: "popInOut 1.5s ease-in-out infinite",
          })}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#22c55e",
              fontSize: { xs: "0.875rem", sm: "1.125rem" },
              textAlign: "center",
            }}
          >
            Click here to download our Dreamey Vids App
          </Typography>
        </Paper>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {selfInfo ? (
        <Card
          sx={{
            mb: 2.5,
            borderRadius: 3.5,
            overflow: "hidden",
            position: "relative",
            border: "1px solid",
            borderColor: alpha(isDark ? portal.cyan : theme.palette.divider, isDark ? 0.22 : 0.12),
            background: isDark
              ? `linear-gradient(165deg, ${alpha("#1a1f30", 0.97)} 0%, ${alpha("#12151f", 0.94)} 45%, ${alpha("#0c0e14", 0.98)} 100%)`
              : alpha(theme.palette.background.paper, 0.98),
            backdropFilter: "blur(16px)",
            boxShadow: isDark
              ? `0 12px 48px ${alpha("#000", 0.45)}, 0 0 0 1px ${alpha("#fff", 0.04)} inset, 0 -1px 0 ${alpha(portal.indigo, 0.12)} inset, 0 0 40px ${alpha(portal.cyan, 0.06)}`
              : `0 12px 40px ${alpha(theme.palette.primary.main, 0.07)}, 0 1px 0 ${alpha("#fff", 0.8)} inset`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${portal.cyan} 0%, ${portal.indigo} 38%, ${portal.purple} 68%, ${portal.pink} 100%)`,
              opacity: isDark ? 0.92 : 0.85,
            },
          }}
        >
          <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, pt: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "center",
                  columnGap: { xs: 0.75, sm: 1 },
                  width: "100%",
                  py: 0.25,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <PersonIcon
                    sx={{
                      color: portal.amber,
                      fontSize: 26,
                      filter: isDark ? `drop-shadow(0 0 10px ${alpha(portal.amber, 0.35)})` : "none",
                    }}
                  />
                  <Typography
                    component={RouterLink}
                    to="/dashboard/ad-streak"
                    variant="h6"
                    title="Go to My Activity"
                    sx={{
                      ...portalGradientTextSx({ fontWeight: 800, letterSpacing: "0.06em" }),
                      fontSize: "1.05rem",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      textAlign: "left",
                      textDecoration: "none",
                      borderBottom: `2px solid ${alpha(portal.cyan, 0.45)}`,
                      textUnderlineOffset: "0.2em",
                      pb: 0.15,
                      cursor: "pointer",
                      transition: "opacity 0.2s, border-color 0.2s",
                      "&:hover": {
                        opacity: 0.92,
                        borderBottomColor: alpha(portal.cyan, 0.75),
                      },
                    }}
                  >
                    YOU
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  flexWrap="nowrap"
                  spacing={1}
                  sx={{
                    gap: { xs: 0.45, sm: 0.85 },
                    flexShrink: 0,
                    justifySelf: "end",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, isDark ? 0.35 : 0.25),
                      borderRadius: 999,
                      px: { xs: 0.55, sm: 1.1, md: 1.25 },
                      py: 0.45,
                      flexShrink: 0,
                      maxWidth: "100%",
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#152018", 0.95)} 0%, ${alpha("#0f1510", 0.98)} 100%)`
                        : alpha(theme.palette.success.main, 0.08),
                      boxShadow: isDark ? `0 0 20px ${alpha(theme.palette.success.main, 0.08)}` : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.light,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "right",
                        letterSpacing: "0.02em",
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        maxWidth: { xs: "min(9.5rem, 38vw)", md: `${selfStatsLayoutChars.rankChars}ch` },
                      }}
                    >
                      {selfInfo.rank}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay)
                        ? alpha(theme.palette.divider, isDark ? 0.35 : 0.2)
                        : alpha(theme.palette.warning.main, 0.45),
                      borderRadius: 999,
                      px: { xs: 0.55, sm: 1.1, md: 1.25 },
                      py: 0.45,
                      flexShrink: 0,
                      maxWidth: "100%",
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha("#1c1618", 0.95)} 0%, ${alpha("#141012", 0.98)} 100%)`
                        : alpha(theme.palette.background.paper, 0.95),
                      boxShadow: !isStreakGoalMetToday(selfInfo.adsWatchedToday, selfInfo.goalPerDay) && isDark
                        ? `0 0 18px ${alpha(theme.palette.warning.main, 0.12)}`
                        : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "common.white",
                        textAlign: "right",
                        letterSpacing: "0.03em",
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      }}
                    >
                      {selfInfo.currentStreak} 🔥
                    </Typography>
                  </Box>
                </Stack>
              </Box>

            <Box
              sx={{
                pt: 1,
                pb: 0.25,
                borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.12)}`,
                background: isDark
                  ? `linear-gradient(180deg, ${alpha(portal.indigo, 0.06)} 0%, transparent 100%)`
                  : alpha(portal.cyan, 0.03),
                borderRadius: 2,
                px: { xs: 0.5, sm: 0.75 },
                mx: { xs: -0.5, sm: -0.75 },
              }}
            >
              <SectionEyebrow sx={{ mb: 1, pl: 0.25 }}>Progress & milestones</SectionEyebrow>
            <Stack spacing={1.25} sx={{ width: "100%" }}>
              <ThinLadderBar
                theme={theme}
                emoji="🎯"
                fillPercent={selfInfo.todayGoalPercent ?? 0}
                label={`Today's goal progress (${selfInfo.adsWatchedToday ?? 0}/${selfInfo.goalPerDay ?? 10})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="👤"
                fillPercent={selfInfo.referralsBarPercent ?? 0}
                label={`Direct referrals progress toward rank tiers (counts ${RANK_BAR_REFERRAL_THRESHOLDS.join(
                  ", "
                )})`}
              />
              <ThinLadderBar
                theme={theme}
                emoji="📶"
                fillPercent={selfInfo.levelsBarPercent ?? 0}
                label={`Network depth progress toward rank tiers (depth ${RANK_BAR_DEPTH_THRESHOLDS.join(", ")})`}
              />
            </Stack>
            </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card
        sx={{
          borderRadius: 3.5,
          overflow: "hidden",
          position: "relative",
          border: "1px solid",
          borderColor: alpha(isDark ? portal.pink : theme.palette.divider, isDark ? 0.24 : 0.12),
          background: isDark
            ? `linear-gradient(165deg, ${alpha("#1f1824", 0.96)} 0%, ${alpha("#14121a", 0.94)} 50%, ${alpha("#0c0b10", 0.98)} 100%)`
            : alpha(theme.palette.background.paper, 0.98),
          backdropFilter: "blur(16px)",
          boxShadow: isDark
            ? `0 12px 48px ${alpha("#000", 0.4)}, inset 0 1px 0 ${alpha(portal.pink, 0.06)}, 0 0 36px ${alpha(portal.pink, 0.05)}`
            : `0 12px 36px ${alpha(theme.palette.primary.main, 0.06)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${portal.pink} 0%, ${portal.purple} 45%, ${portal.cyan} 100%)`,
            opacity: isDark ? 0.9 : 0.75,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, pt: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{
              mb: 0.75,
              gap: 1,
              pb: 1.15,
              borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.1)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.15} flexWrap="wrap" useFlexGap>
              <GroupsIcon
                sx={{
                  color: portal.pink,
                  fontSize: 28,
                  filter: isDark ? `drop-shadow(0 0 12px ${alpha(portal.pink, 0.35)})` : "none",
                }}
              />
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  letterSpacing: "0.04em",
                  color: portal.pink,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                }}
              >
                My Referrals
              </Typography>
              <Chip
                size="small"
                label={referrals.length}
                sx={{
                  fontWeight: 700,
                  height: 26,
                  color: "common.white",
                  border: "1px solid",
                  borderColor: alpha(portal.cyan, 0.4),
                  bgcolor: alpha(portal.cyan, 0.14),
                  boxShadow: isDark ? `0 0 16px ${alpha(portal.cyan, 0.15)}` : "none",
                }}
              />
            </Stack>
            {referrals.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                {anyDownlineAdGoalOpen && (
                  <Tooltip title="Today's ad goal not completed yet (UTC day) for one or more downline members.">
                    <WarningAmberIcon sx={{ fontSize: 20, color: "warning.main" }} aria-hidden />
                  </Tooltip>
                )}
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: "0.7rem",
                    color: anyDownlineAdGoalOpen ? "warning.light" : "text.secondary",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatUtcMidnightCountdown(utcMidnightRemainingMs)}
                </Typography>
              </Stack>
            )}
          </Stack>

          {referrals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No referrals found.
            </Typography>
          ) : (
            <Stack spacing={1.35}>
              {referrals.map((ref) => (
                  <Box
                    key={ref.userId}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, isDark ? 0.4 : 0.12),
                      borderRadius: 3,
                      p: { xs: 1.35, sm: 1.5 },
                      pl: { xs: 1.6, sm: 1.85 },
                      background: isDark
                        ? `linear-gradient(150deg, ${alpha("#1a1d28", 0.92)} 0%, ${alpha("#12141c", 0.95)} 100%)`
                        : alpha(theme.palette.background.paper, 0.97),
                      boxShadow: isDark
                        ? `inset 0 1px 0 ${alpha("#fff", 0.05)}, 0 4px 20px ${alpha("#000", 0.2)}`
                        : `0 2px 12px ${alpha(theme.palette.primary.main, 0.04)}`,
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "12%",
                        bottom: "12%",
                        width: 4,
                        borderRadius: "0 4px 4px 0",
                        background: `linear-gradient(180deg, ${portal.cyan} 0%, ${portal.indigo} 50%, ${portal.pink} 100%)`,
                        opacity: 0.65,
                        transition: "opacity 0.2s ease",
                      },
                      "&:hover": {
                        borderColor: alpha(portal.cyan, isDark ? 0.28 : 0.22),
                        boxShadow: isDark
                          ? `inset 0 1px 0 ${alpha("#fff", 0.06)}, 0 8px 28px ${alpha("#000", 0.35)}`
                          : `0 6px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                        "&::before": { opacity: 1 },
                      },
                    }}
                  >
                    <Stack spacing={1.1} sx={{ width: "100%", minWidth: 0 }}>
                      <ReferralCardNamePillsRow
                        referral={ref}
                        theme={theme}
                        isDark={isDark}
                        referralLayoutChars={referralLayoutChars}
                      />
                      <ThinLadderBar
                        theme={theme}
                        emoji="🎯"
                        fillPercent={ref.todayGoalPercent ?? 0}
                        label={`Referral today's goal progress (${ref.adsWatchedToday ?? 0}/${ref.goalPerDay ?? 10})`}
                      />
                    </Stack>
                  </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={copySnack.open}
        autoHideDuration={2600}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setCopySnack((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopySnack((s) => ({ ...s, open: false }))}
          severity={copySnack.severity}
          variant="filled"
          elevation={6}
          sx={{
            alignItems: "center",
            fontWeight: 600,
            fontSize: "0.875rem",
            py: 0.5,
            px: 1,
            minWidth: "unset",
            maxWidth: { xs: "92vw", sm: 360 },
          }}
        >
          {copySnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
